/**
 * How a full-screen moment fills the viewer.
 *
 * FILL ONLY WHAT MATCHES THE SCREEN (FEAT-040, Nation 2026-08-07: "the actual
 * photo is what I was talking about … I think what we're doing is cropping in,
 * which is causing some issues"). An in-app capture is made at the phone's own
 * shape, so it fills edge-to-edge and loses nothing. Anything ELSE — a 3:4
 * camera-roll photo, a 9:16 clip from an older phone, a wide moment — shows
 * WHOLE, and the viewer paints a blurred backdrop of the same picture behind
 * it instead of dead black bars. This supersedes FEAT-027 Wave 3's
 * "portrait always fills": that call predated real camera-roll photos, whose
 * sides the old rule silently cropped off.
 *
 * Two sources of shape, in this order:
 *  1. the media's own intrinsic size once it has loaded — exact;
 *  2. before any bytes arrive, the aspect ratio carried in the `thumbhash`
 *     we already store on every message, story and saved item — so the blur
 *     placeholder and the first painted frame pick the SAME fit and nothing
 *     visibly flips underneath the reader.
 */
import { thumbHashToApproximateAspectRatio } from 'thumbhash';
import { frameViewport } from './deviceOrientation';
import { thumbhashBytes } from './thumbhashUrl';

export type MediaFitClass = 'object-cover' | 'object-contain';

/** An unknown shape keeps today's behaviour: fill. */
export const DEFAULT_MEDIA_FIT: MediaFitClass = 'object-cover';

/**
 * How far a moment's shape may sit from the screen's own and still FILL.
 * Wide enough that a capture from any modern phone (and the thumbhash's
 * coarse quantization of it) lands inside; a 9:16 clip (~22% off a 19.5:9
 * screen) and a 3:4 camera-roll photo (~62% off) land outside and show whole.
 */
const COVER_TOLERANCE = 0.15;

function screenAspect(): number {
  const { width, height } = frameViewport();
  return width > 0 && height > 0 ? width / height : 9 / 19.5;
}

/** Screen-shaped fills; every other shape shows the whole picture. */
export function fitForAspect(ratio: number | null | undefined): MediaFitClass {
  if (!ratio || !Number.isFinite(ratio) || ratio <= 0) return DEFAULT_MEDIA_FIT;
  const screen = screenAspect();
  return Math.abs(ratio - screen) / screen <= COVER_TOLERANCE
    ? 'object-cover'
    : 'object-contain';
}

/** Same rule from raw pixels. A zero or missing dimension means "don't know". */
export function fitForSize(
  width: number | null | undefined,
  height: number | null | undefined
): MediaFitClass {
  if (!width || !height || width <= 0 || height <= 0) return DEFAULT_MEDIA_FIT;
  return fitForAspect(width / height);
}

/** Intrinsic pixels of a loaded <img>/<video>, or null while still unknown. */
export function intrinsicSize(
  el: HTMLImageElement | HTMLVideoElement | null | undefined
): { width: number; height: number } | null {
  if (!el) return null;
  const width = 'videoWidth' in el ? el.videoWidth : el.naturalWidth;
  const height = 'videoHeight' in el ? el.videoHeight : el.naturalHeight;
  if (!width || !height) return null;
  return { width, height };
}

/**
 * Aspect ratio (w/h) encoded in a thumbhash, memoized per hash like the
 * data-URL decode beside it. Coarse by design — the hash quantizes the ratio,
 * so 16:9 reads back as 1.75 and 9:16 as 0.57. That is miles from the 1.0
 * boundary; only near-square media could read the wrong side of it, and then
 * the intrinsic size corrects it on load.
 */
const aspectCache = new Map<string, number | null>();

export function thumbhashAspect(hash: string | null | undefined): number | null {
  if (!hash) return null;
  const cached = aspectCache.get(hash);
  if (cached !== undefined) return cached;
  const bytes = thumbhashBytes(hash);
  let ratio: number | null = null;
  if (bytes) {
    try {
      const value = thumbHashToApproximateAspectRatio(bytes);
      if (Number.isFinite(value) && value > 0) ratio = value;
    } catch {
      ratio = null;
    }
  }
  aspectCache.set(hash, ratio);
  return ratio;
}

/** The pre-paint fit: what the thumbhash says, or fill when there is none. */
export function fitForThumbhash(hash: string | null | undefined): MediaFitClass {
  return fitForAspect(thumbhashAspect(hash));
}
